import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { requirementService } from '../../services/requirementService';
import ConfirmModal from '../../components/Common/ConfirmModal';
import Toast from '../../components/Common/Toast';
import { formatDateLocaleLima } from '../../utils/dateUtils';
import MutateGate from '../../components/Common/MutateGate';
import { useCanMutate } from '../../hooks/useCanMutate';
import RequirementPdfButton from '../../components/Requirements/RequirementPdfButton';
import BuyerUserCell from '../../components/Common/BuyerUserCell';
import ComprasProjectFilterSelect from '../../components/Common/ComprasProjectFilterSelect';
import { useModuleSettings } from '../../contexts/ModuleSettingsContext';
import { formatLineProjectsLabel } from '../../utils/lineProjectLabels';

const RequirementsIndex = () => {
  const canMutate = useCanMutate();
  const { productsProjectEnabled } = useModuleSettings();
  const [items, setItems] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [userFilter, setUserFilter] = useState('all');
  const [projectFilter, setProjectFilter] = useState('all');
  const [toggleTarget, setToggleTarget] = useState(null);
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (searchTerm) params.search = searchTerm;
      if (productsProjectEnabled && projectFilter !== 'all') params.project_id = projectFilter;
      const res = await requirementService.getRequirements(params);
      if (res.success) setItems(res.data || []);
    } catch {
      setToast({ isOpen: true, message: 'Error al cargar requerimientos', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!productsProjectEnabled) return;
    requirementService.getFormData().then((res) => {
      if (res.success) setProjects(res.data?.projects || []);
    }).catch(() => {});
  }, [productsProjectEnabled]);

  useEffect(() => {
    load();
  }, [statusFilter, projectFilter, productsProjectEnabled]);

  const creatorOptions = useMemo(() => {
    const map = new Map();
    items.forEach((row) => {
      const id = row.creator?.id ?? row.created_by;
      const name = row.creator?.name;
      if (id && name && !map.has(String(id))) map.set(String(id), name);
    });
    return Array.from(map.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name, 'es'));
  }, [items]);

  const filteredItems = useMemo(() => {
    if (userFilter === 'all') return items;
    return items.filter((row) => String(row.creator?.id ?? row.created_by ?? '') === String(userFilter));
  }, [items, userFilter]);

  const handleSearch = (e) => {
    e.preventDefault();
    load();
  };

  const handlePdfSuccess = (req) => {
    setToast({
      isOpen: true,
      message: `PDF de ${req.code} descargado`,
      type: 'success',
    });
  };

  const handlePdfError = (message) => {
    setToast({
      isOpen: true,
      message: message || 'Error al generar el PDF',
      type: 'error',
    });
  };

  const renderPdfAction = (req) => (
    <RequirementPdfButton
      requirementId={req.id}
      onSuccess={handlePdfSuccess}
      onError={handlePdfError}
    />
  );

  const confirmToggle = async () => {
    if (!toggleTarget) return;
    try {
      const res = await requirementService.toggleStatus(toggleTarget.id);
      setToast({ isOpen: true, message: res.message, type: 'success' });
      setToggleTarget(null);
      load();
    } catch (err) {
      setToast({
        isOpen: true,
        message: err.response?.data?.message || 'Error al cambiar estado',
        type: 'error',
      });
    }
  };

  return (
    <div className="p-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-800">Requerimientos</h1>
            <p className="text-gray-600 mt-1">Solicitudes de productos con o sin proveedor (sin costos)</p>
          </div>
          <MutateGate>
            <Link
              to="/requirements/create"
              className="inline-flex items-center px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700"
            >
              + Nuevo requerimiento
            </Link>
          </MutateGate>
        </div>

        <form onSubmit={handleSearch} className="mt-6 flex flex-wrap gap-3 items-end">
          <div>
            <label className="block text-xs text-gray-600 mb-1">Estado</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2"
            >
              <option value="all">Todos</option>
              <option value="issued">Emitidos</option>
              <option value="cancelled">Cancelados</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Usuario</label>
            <select
              value={userFilter}
              onChange={(e) => setUserFilter(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 min-w-[160px]"
            >
              <option value="all">Todos</option>
              {creatorOptions.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.name}
                </option>
              ))}
            </select>
          </div>
          {productsProjectEnabled && (
            <div>
              <label className="block text-xs text-gray-600 mb-1">Proyecto</label>
              <ComprasProjectFilterSelect
                value={projectFilter}
                onChange={(e) => setProjectFilter(e.target.value)}
                projects={projects}
              />
            </div>
          )}
          <div className="flex-1 min-w-[200px]">
            <label className="block text-xs text-gray-600 mb-1">Buscar</label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Código u observación..."
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
            />
          </div>
          <button type="submit" className="px-4 py-2 bg-gray-800 text-white rounded-lg">
            Buscar
          </button>
        </form>

        <div className="mt-6 bg-white rounded-xl border border-gray-200 overflow-hidden">
          {loading ? (
            <p className="p-8 text-center text-gray-500">Cargando...</p>
          ) : filteredItems.length === 0 ? (
            <p className="p-8 text-center text-gray-500">No hay requerimientos</p>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Código</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Proveedor</th>
                  {productsProjectEnabled && (
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Proyecto</th>
                  )}
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ítems</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Avance</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredItems.map((req) => {
                  const pct = req.fulfillment_percent ?? 0;
                  const statusLabel =
                    req.status === 'cancelled'
                      ? 'Cancelado'
                      : pct >= 100
                      ? 'Completado'
                      : pct > 0
                      ? 'Parcial'
                      : 'Emitido';
                  return (
                    <tr key={req.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <Link to={`/requirements/${req.id}/edit`} className="font-mono text-teal-700">
                          {req.code}
                        </Link>
                        <BuyerUserCell creator={req.creator} />
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {formatDateLocaleLima(req.request_date)}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {req.supplier?.name || (
                          <span className="text-gray-400">Interno</span>
                        )}
                      </td>
                      {productsProjectEnabled && (
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {formatLineProjectsLabel(req.items)}
                        </td>
                      )}
                      <td className="px-6 py-4 text-sm">{req.items?.length || 0}</td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <div className="w-20 bg-gray-200 rounded-full h-2">
                            <div className="bg-teal-600 h-2 rounded-full" style={{ width: `${pct}%` }} />
                          </div>
                          <span className="text-xs text-gray-600">{pct}%</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`px-2 py-1 text-xs rounded-full ${
                            req.status === 'cancelled'
                              ? 'bg-red-100 text-red-800'
                              : pct >= 100
                              ? 'bg-blue-100 text-blue-800'
                              : pct > 0
                              ? 'bg-amber-100 text-amber-800'
                              : 'bg-green-100 text-green-800'
                          }`}
                        >
                          {statusLabel}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm">
                        <div className="flex items-center gap-3">
                          <Link to={`/requirements/${req.id}/edit`} className="text-purple-600 hover:text-purple-900">
                            {canMutate && req.status !== 'cancelled' ? 'Editar' : 'Ver'}
                          </Link>
                          {renderPdfAction(req)}
                          <MutateGate>
                            <button
                              type="button"
                              onClick={() => setToggleTarget(req)}
                              className={req.status === 'issued' ? 'text-orange-600' : 'text-green-600'}
                            >
                              {req.status === 'issued' ? 'Cancelar' : 'Reactivar'}
                            </button>
                          </MutateGate>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <ConfirmModal
        isOpen={!!toggleTarget}
        onClose={() => setToggleTarget(null)}
        onConfirm={confirmToggle}
        title={toggleTarget?.status === 'issued' ? 'Cancelar requerimiento' : 'Reactivar requerimiento'}
        message={
          toggleTarget
            ? `¿${toggleTarget.status === 'issued' ? 'Cancelar' : 'Reactivar'} el requerimiento "${toggleTarget.code}"?`
            : ''
        }
        confirmText="Sí"
        cancelText="No"
      />
      <Toast
        isOpen={toast.isOpen}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, isOpen: false })}
      />
    </div>
  );
};

export default RequirementsIndex;




